import React from 'react';
import './SettingsPanel.css';
import { UITheme, BoardTheme } from '../utils/themes';
import { PieceSet } from '../utils/pieceSets';

/**
 * Props for SettingsPanel component
 */
interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  
  // Sound settings
  soundsEnabled: boolean;
  onToggleSound: () => void;
  soundVolume: number;
  onVolumeChange: (volume: number) => void;
  
  // Board settings
  flipped: boolean;
  onFlipBoard: () => void;
  showCoordinates?: boolean;
  onToggleCoordinates?: () => void;
  
  // Animation settings
  animationsEnabled?: boolean;
  onToggleAnimations?: () => void;
  
  // Theme settings
  uiTheme: UITheme;
  onUIThemeChange: (theme: UITheme) => void;
  boardTheme: BoardTheme;
  onBoardThemeChange: (theme: BoardTheme) => void;
  
  // Piece set settings
  pieceSet: PieceSet;
  onPieceSetChange: (set: PieceSet) => void;
}

/**
 * Settings panel component - modal dialog with game settings
 * 
 * Features:
 * - Sound on/off toggle
 * - Volume slider
 * - Board flip toggle
 * - Coordinate display toggle
 * - Animation toggle
 * - UI theme selection (light/dark)
 * - Board color theme selection
 * - Piece set selection
 * - Modal overlay with click-to-close
 * - Organized sections for related settings
 */
export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  isOpen,
  onClose,
  soundsEnabled,
  onToggleSound,
  soundVolume,
  onVolumeChange,
  flipped,
  onFlipBoard,
  showCoordinates = true,
  onToggleCoordinates,
  animationsEnabled = true,
  onToggleAnimations,
  uiTheme,
  onUIThemeChange,
  boardTheme,
  onBoardThemeChange,
  pieceSet,
  onPieceSetChange
}) => {
  if (!isOpen) return null;

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(e.target.value);
    onVolumeChange(volume);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="settings-overlay" onClick={handleOverlayClick}>
      <div className="settings-panel">
        <div className="settings-header">
          <h2>⚙️ Settings</h2>
          <button className="close-button" onClick={onClose} title="Close settings">
            ✕
          </button>
        </div>
        
        <div className="settings-content">
          {/* Sound Settings Section */}
          <section className="settings-section">
            <h3 className="section-title">🔊 Sound</h3>
            
            <div className="setting-item">
              <label className="setting-label">Enable Sound Effects</label>
              <button
                className={`toggle-button ${soundsEnabled ? 'active' : ''}`}
                onClick={onToggleSound}
                title={soundsEnabled ? 'Disable sounds' : 'Enable sounds'}
              >
                <span className="toggle-slider"></span>
              </button>
            </div>
            
            <div className="setting-item">
              <label className="setting-label">Volume</label>
              <div className="volume-control-inline">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={soundVolume}
                  onChange={handleVolumeChange}
                  disabled={!soundsEnabled}
                  className="volume-slider-inline"
                  style={
                    {
                      '--slider-value': soundVolume
                    } as React.CSSProperties
                  }
                  title={`Volume: ${Math.round(soundVolume * 100)}%`}
                />
                <span className="volume-percentage-inline">
                  {Math.round(soundVolume * 100)}%
                </span>
              </div>
            </div>
          </section>

          {/* Board Settings Section */}
          <section className="settings-section">
            <h3 className="section-title">🎨 Board</h3>
            
            <div className="setting-item">
              <label className="setting-label">Flip Board (Black Perspective)</label>
              <button
                className={`toggle-button ${flipped ? 'active' : ''}`}
                onClick={onFlipBoard}
                title={flipped ? 'View from white perspective' : 'View from black perspective'}
              >
                <span className="toggle-slider"></span>
              </button>
            </div>
            
            {onToggleCoordinates && (
              <div className="setting-item">
                <label className="setting-label">Show Coordinates</label>
                <button
                  className={`toggle-button ${showCoordinates ? 'active' : ''}`}
                  onClick={onToggleCoordinates}
                  title={showCoordinates ? 'Hide coordinates' : 'Show coordinates'}
                >
                  <span className="toggle-slider"></span>
                </button>
              </div>
            )}
          </section>

          {/* Theme Settings Section */}
          <section className="settings-section">
            <h3 className="section-title">🎨 Appearance</h3>
            
            <div className="setting-item">
              <label className="setting-label">UI Theme</label>
              <select
                value={uiTheme}
                onChange={(e) => onUIThemeChange(e.target.value as UITheme)}
                className="theme-select"
                title="Select light or dark mode"
              >
                <option value="light">☀️ Light</option>
                <option value="dark">🌙 Dark</option>
              </select>
            </div>
            
            <div className="setting-item">
              <label className="setting-label">Board Colors</label>
              <select
                value={boardTheme}
                onChange={(e) => onBoardThemeChange(e.target.value as BoardTheme)}
                className="theme-select"
                title="Select board color scheme"
              >
                <option value="classic">Classic (Beige/Brown)</option>
                <option value="wood">Wood (Light/Dark Brown)</option>
                <option value="blue">Blue (Light Blue/Navy)</option>
                <option value="green">Green (Light Green/Forest)</option>
                <option value="gray">Gray (Light/Dark Gray)</option>
              </select>
            </div>
            
            <div className="setting-item">
              <label className="setting-label">Piece Set</label>
              <select
                value={pieceSet}
                onChange={(e) => onPieceSetChange(e.target.value as PieceSet)}
                className="theme-select"
                title="Select piece style"
              >
                <option value="classic">Classic (Filled)</option>
                <option value="modern">Modern (Bold)</option>
                <option value="alpha">Alpha (Letters)</option>
                <option value="cases">Cases (Large)</option>
              </select>
            </div>
          </section>

          {/* Animation Settings Section */}
          {onToggleAnimations && (
            <section className="settings-section">
              <h3 className="section-title">✨ Animations</h3>
              
              <div className="setting-item">
                <label className="setting-label">Enable Move Animations</label>
                <button
                  className={`toggle-button ${animationsEnabled ? 'active' : ''}`}
                  onClick={onToggleAnimations}
                  title={animationsEnabled ? 'Disable animations' : 'Enable animations'}
                >
                  <span className="toggle-slider"></span>
                </button>
              </div>
            </section>
          )}
        </div>
        
        <div className="settings-footer">
          <button className="close-settings-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
